import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL, getDispatcherToken } from './DispatcherApp';
import DriverScheduleModal from './DriverScheduleModal';
import DriverDayTimeline from './DriverDayTimeline';
import DispatcherTimelineBoard from './DispatcherTimelineBoard';
import { scheduleOwner } from './scheduleOwner';
import { ChevronLeft, ChevronRight, RefreshCw, SlidersHorizontal, Plus, LayoutGrid, Search, BarChart3, ClipboardList, Car } from 'lucide-react';
import AddScheduleModal from './AddScheduleModal';
import EditScheduleModal from './EditScheduleModal';
import MonthSelect, { getMonthValue } from '../../components/admin/MonthSelect';
import shonanPalmLogo from '../../assets/shonan-palm-gold-transparent.png';
import { addDaysToDateKey, getTokyoDateKey, getTokyoMonthRange } from '../../utils/tokyoDate';

const formatYen = (amount) => `¥${Math.round(amount || 0).toLocaleString()}`;
const formatVisibleYen = (amount, canViewPrice = true) => (
  canViewPrice ? formatYen(amount) : '无权限'
);
const getSourceLabel = (sourceBucket) => ({
  dispatcher_added: '调度加入',
  driver_added: '司机加入',
  admin_added: '管理员加入',
}[sourceBucket] || '来源');
const SOURCE_FILTER_OPTIONS = [
  { key: '', label: '全部来源' },
  { key: 'dispatcher_added', label: '调度加入' },
  { key: 'driver_added', label: '司机加入' },
  { key: 'admin_added', label: '管理员加入' },
];
const buildSupplierAccountLabel = (account) => {
  if (!account) return '';
  const displayName = account.company_name || account.name || account.email || account.customer_code || account.account || '';
  const code = account.customer_code || account.email || account.account || '';
  return code ? `${displayName} (${code})` : displayName;
};
const buildSupplierAccountSearchText = (account) => [
  account?.company_name,
  account?.name,
  account?.label,
  account?.customer_code,
  account?.email,
  account?.account,
].filter(Boolean).join(' ').toLowerCase();
const DISPATCHER_OVERVIEW_REFRESH_INTERVAL_MS = 15000;
const DEFAULT_DRIVER_VIEW_PREFERENCES = { order: [], hidden: {} };

function getDispatcherDriverPreferenceKey(dispatcher) {
  return `dispatcher-driver-overview-preferences:${dispatcher?.id || dispatcher?.phone || 'anonymous'}`;
}

function sortDriversByPreference(drivers, driverViewPreferences) {
  const orderIndex = new Map((driverViewPreferences.order || []).map((driverId, index) => [driverId, index]));
  return [...drivers].sort((a, b) => {
    const aIndex = orderIndex.has(a.id) ? orderIndex.get(a.id) : Number.MAX_SAFE_INTEGER;
    const bIndex = orderIndex.has(b.id) ? orderIndex.get(b.id) : Number.MAX_SAFE_INTEGER;
    if (aIndex !== bIndex) return aIndex - bIndex;
    return (a.name || '').localeCompare(b.name || '');
  });
}

function buildCompleteDriverPreferenceOrder(drivers, driverViewPreferences) {
  const currentIds = new Set(drivers.map(driver => driver.id));
  const savedOrder = (driverViewPreferences.order || []).filter(driverId => currentIds.has(driverId));
  const savedSet = new Set(savedOrder);
  return [
    ...savedOrder,
    ...drivers.filter(driver => !savedSet.has(driver.id)).map(driver => driver.id),
  ];
}

export default function DispatcherDashboard({ dispatcher, onLogout }) {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scheduleData, setScheduleData] = useState({});
  
  // 视图设置
  const [viewMode, setViewMode] = useState('day');
  const [dateRange, setDateRange] = useState('3days');
  const [overviewStartDate, setOverviewStartDate] = useState(getTokyoDateKey());
  const [selectedSlotDate, setSelectedSlotDate] = useState(getTokyoDateKey());
  
  // 模态框
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDriverViewSettings, setShowDriverViewSettings] = useState(false);
  const [driverViewPreferences, setDriverViewPreferences] = useState(DEFAULT_DRIVER_VIEW_PREFERENCES);
  const [driverViewPreferencesLoaded, setDriverViewPreferencesLoaded] = useState(false);
  
  // 搜索和统计
  const [activeSection, setActiveSection] = useState('overview'); // overview | search | stats
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [supplierAccounts, setSupplierAccounts] = useState([]);
  const [supplierAccountSearch, setSupplierAccountSearch] = useState('');
  const [selectedSupplierAccount, setSelectedSupplierAccount] = useState('');
  const [statsData, setStatsData] = useState(null);
  const [statsRange, setStatsRange] = useState('month');
  const [statsDateFrom, setStatsDateFrom] = useState('');
  const [statsDateTo, setStatsDateTo] = useState('');
  const [driverMonthlyStatsMonth, setDriverMonthlyStatsMonth] = useState(getMonthValue());
  const [driverMonthlyStatsDispatcherId, setDriverMonthlyStatsDispatcherId] = useState('');
  const [driverMonthlyStatsDriverId, setDriverMonthlyStatsDriverId] = useState('');
  const [driverMonthlyStats, setDriverMonthlyStats] = useState(null);
  const [driverMonthlyStatsLoading, setDriverMonthlyStatsLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const scheduleRefreshInFlightRef = useRef(false);
  const driverPreferenceStorageKey = useMemo(
    () => getDispatcherDriverPreferenceKey(dispatcher),
    [dispatcher]
  );

  // 计算日期范围
  const dateColumns = useMemo(() => {
    const todayKey = getTokyoDateKey();
    const startKey = overviewStartDate || todayKey;
    const dates = [];
    let daysCount = 3;
    
    if (dateRange === 'today') daysCount = 1;
    else if (dateRange === 'tomorrow') daysCount = 1;
    else if (dateRange === '3days') daysCount = 3;
    else if (dateRange === '7days') daysCount = 7;
    
    for (let i = 0; i < daysCount; i++) {
      const dateKey = addDaysToDateKey(startKey, i);
      const d = new Date(`${dateKey}T00:00:00+09:00`);
      dates.push({
        date: dateKey,
        label: formatDateLabel(dateKey),
        weekday: ['日', '一', '二', '三', '四', '五', '六'][d.getDay()]
      });
    }
    return dates;
  }, [dateRange, overviewStartDate]);

  // 时段定义
  const timeSlots = [
    { id: 'morning', label: '上午', range: '06:00-12:00', start: 6, end: 12 },
    { id: 'afternoon', label: '下午', range: '12:00-18:00', start: 12, end: 18 },
    { id: 'evening', label: '晚上', range: '18:00-24:00', start: 18, end: 24 }
  ];

  function formatDateLabel(dateKey) {
    const todayKey = getTokyoDateKey();
    if (dateKey === todayKey) return '今天';
    if (dateKey === addDaysToDateKey(todayKey, 1)) return '明天';
    if (dateKey === addDaysToDateKey(todayKey, 2)) return '后天';
    const [, month, day] = String(dateKey).split('-');
    return `${Number(month)}/${Number(day)}`;
  }

  useEffect(() => {
    setDriverViewPreferencesLoaded(false);
    try {
      const saved = window.localStorage?.getItem(driverPreferenceStorageKey);
      setDriverViewPreferences(saved ? JSON.parse(saved) : DEFAULT_DRIVER_VIEW_PREFERENCES);
    } catch (err) {
      setDriverViewPreferences(DEFAULT_DRIVER_VIEW_PREFERENCES);
    } finally {
      setDriverViewPreferencesLoaded(true);
    }
  }, [driverPreferenceStorageKey]);

  useEffect(() => {
    if (!driverViewPreferencesLoaded) return;
    try {
      window.localStorage?.setItem(driverPreferenceStorageKey, JSON.stringify(driverViewPreferences));
    } catch (err) {
      console.warn('Failed to save dispatcher driver view preferences:', err);
    }
  }, [driverPreferenceStorageKey, driverViewPreferences, driverViewPreferencesLoaded]);

  const driverRefreshInFlightRef = useRef(false);
  const expiredSessionRef = useRef(false);
  const fetchDrivers = useCallback(async () => {
    if (driverRefreshInFlightRef.current || expiredSessionRef.current) return;
    driverRefreshInFlightRef.current = true;
    try {
      const response = await fetch(`${API_BASE_URL}/dispatcher/drivers`, {
        headers: { 'Authorization': `Bearer ${getDispatcherToken()}` }
      });
      if (response.status === 401 || response.status === 403) {
        expiredSessionRef.current = true;
        setDrivers([]);
        setScheduleData({});
        window.alert('登录或司机管理权限已更新，请重新登录以加载最新司机范围。');
        onLogout();
      } else if (response.ok) {
        const nextDrivers = await response.json();
        setDrivers(current => JSON.stringify(current) === JSON.stringify(nextDrivers) ? current : nextDrivers);
      }
    } catch (err) {
      console.error('Failed to fetch drivers:', err);
    } finally {
      driverRefreshInFlightRef.current = false;
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    fetchDrivers();
    const refreshVisibleDrivers = () => {
      if (!document.hidden) fetchDrivers();
    };
    const intervalId = setInterval(refreshVisibleDrivers, DISPATCHER_OVERVIEW_REFRESH_INTERVAL_MS);
    window.addEventListener('focus', refreshVisibleDrivers);
    document.addEventListener('visibilitychange', refreshVisibleDrivers);
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', refreshVisibleDrivers);
      document.removeEventListener('visibilitychange', refreshVisibleDrivers);
    };
  }, [fetchDrivers]);

  const orderedOverviewDrivers = useMemo(
    () => sortDriversByPreference(drivers, driverViewPreferences),
    [drivers, driverViewPreferences]
  );
  const visibleOverviewDrivers = useMemo(
    () => orderedOverviewDrivers.filter(driver => !driverViewPreferences.hidden?.[driver.id]),
    [driverViewPreferences, orderedOverviewDrivers]
  );
  const hiddenOverviewDriverCount = Math.max(0, drivers.length - visibleOverviewDrivers.length);

  const toggleDriverHidden = (driverId) => {
    setDriverViewPreferences(current => ({
      ...current,
      hidden: {
        ...(current.hidden || {}),
        [driverId]: !current.hidden?.[driverId],
      },
    }));
  };

  const moveDriverInPreference = (driverId, direction) => {
    setDriverViewPreferences(current => {
      const order = buildCompleteDriverPreferenceOrder(drivers, current);
      const index = order.indexOf(driverId);
      const targetIndex = index + direction;
      if (index < 0 || targetIndex < 0 || targetIndex >= order.length) return current;
      const nextOrder = [...order];
      [nextOrder[index], nextOrder[targetIndex]] = [nextOrder[targetIndex], nextOrder[index]];
      return { ...current, order: nextOrder };
    });
  };

  const resetDriverViewPreferences = () => {
    setDriverViewPreferences({
      order: drivers.map(driver => driver.id),
      hidden: {},
    });
  };

  const fetchAllSchedules = useCallback(async () => {
    if (scheduleRefreshInFlightRef.current) return;
    scheduleRefreshInFlightRef.current = true;
    const data = {};
    const dateFrom = viewMode === 'timeslot' ? selectedSlotDate : dateColumns[0]?.date;
    const dateTo = viewMode === 'timeslot' ? selectedSlotDate : dateColumns[dateColumns.length - 1]?.date;

    try {
      for (const driver of drivers) {
        try {
          const response = await fetch(
            `${API_BASE_URL}/dispatcher/driver/${driver.id}/schedules?date_from=${dateFrom}&date_to=${dateTo}&include_cancelled=false`,
            { headers: { 'Authorization': `Bearer ${getDispatcherToken()}` } }
          );
          if (response.ok) {
            data[driver.id] = await response.json();
          }
        } catch (err) {
          console.error(`Failed to fetch schedules for ${driver.id}:`, err);
        }
      }
      setScheduleData(data);
    } finally {
      scheduleRefreshInFlightRef.current = false;
    }
  }, [dateColumns, drivers, selectedSlotDate, viewMode]);

  useEffect(() => {
    if (drivers.length > 0) {
      fetchAllSchedules();
    }
  }, [drivers.length, fetchAllSchedules]);

  useEffect(() => {
    if (activeSection !== 'overview' || drivers.length === 0) return undefined;

    const refreshDispatcherOverviewSilently = () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      if (showAddModal || showScheduleModal || editingItem || showDriverViewSettings) return;
      fetchAllSchedules();
    };

    const intervalId = setInterval(refreshDispatcherOverviewSilently, DISPATCHER_OVERVIEW_REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [
    activeSection,
    drivers.length,
    editingItem,
    fetchAllSchedules,
    showAddModal,
    showDriverViewSettings,
    showScheduleModal,
  ]);

  // 获取某司机某天的排班统计
  const getDayStats = (driverId, date) => {
    const driverData = scheduleData[driverId];
    if (!driverData) return { official: 0, driverExtra: 0, dispatcherExtra: 0, total: 0 };
    
    const officialOrders = driverData.official_orders?.filter(o => o.date === date) || [];
    const extras = driverData.extra_schedules?.filter(e => e.date === date) || [];
    const driverExtras = extras.filter(e => e.source_type === 'driver_extra');
    const dispatcherExtras = extras.filter(e => e.source_type === 'dispatcher_extra');
    
    return {
      official: officialOrders.length,
      driverExtra: driverExtras.length,
      dispatcherExtra: dispatcherExtras.length,
      total: officialOrders.length + extras.length
    };
  };

  // 获取某司机某时段的排班统计
  const getSlotStats = (driverId, slot) => {
    const driverData = scheduleData[driverId];
    if (!driverData) return { official: 0, driverExtra: 0, dispatcherExtra: 0, total: 0 };
    
    const isInSlot = (timeStr) => {
      if (!timeStr) return false;
      const [h] = timeStr.split(':').map(Number);
      return h >= slot.start && h < slot.end;
    };
    
    const officialOrders = driverData.official_orders?.filter(o => 
      o.date === selectedSlotDate && isInSlot(o.time)
    ) || [];
    const extras = driverData.extra_schedules?.filter(e => 
      e.date === selectedSlotDate && isInSlot(e.time)
    ) || [];
    const driverExtras = extras.filter(e => e.source_type === 'driver_extra');
    const dispatcherExtras = extras.filter(e => e.source_type === 'dispatcher_extra');
    
    return {
      official: officialOrders.length,
      driverExtra: driverExtras.length,
      dispatcherExtra: dispatcherExtras.length,
      total: officialOrders.length + extras.length
    };
  };

  // 渲染单元格内容
  const renderCell = (stats) => {
    if (stats.total === 0) {
      return (
        <span className="premium-chip-success">空闲</span>
      );
    }
    return (
      <div className="flex items-center gap-1 justify-center flex-wrap">
        {stats.official > 0 && (
          <span className="premium-chip">
            {stats.official}正式
          </span>
        )}
        {stats.driverExtra > 0 && (
          <span className="premium-chip">
            {stats.driverExtra}司机
          </span>
        )}
        {stats.dispatcherExtra > 0 && (
          <span className="premium-chip">
            {stats.dispatcherExtra}调度
          </span>
        )}
      </div>
    );
  };

  const openScheduleModal = (driver) => {
    setSelectedDriver(driver);
    setShowScheduleModal(true);
  };

  const openAddModal = (driver = null) => {
    setSelectedDriver(driver);
    setShowAddModal(true);
  };

  // Search
  const selectedSupplierLabel = useMemo(() => {
    const account = supplierAccounts.find(item => item.account === selectedSupplierAccount);
    return account ? buildSupplierAccountLabel(account) : '';
  }, [selectedSupplierAccount, supplierAccounts]);
  const supplierAccountOptions = useMemo(
    () => [...supplierAccounts].sort((a, b) => buildSupplierAccountLabel(a).localeCompare(buildSupplierAccountLabel(b), 'ja')),
    [supplierAccounts]
  );
  const filteredSupplierAccountOptions = useMemo(() => {
    const query = supplierAccountSearch.trim().toLowerCase();
    const matchedOptions = query
      ? supplierAccountOptions.filter(account => buildSupplierAccountSearchText(account).includes(query))
      : supplierAccountOptions;
    if (!selectedSupplierAccount || matchedOptions.some(account => account.account === selectedSupplierAccount)) {
      return matchedOptions;
    }
    const selected = supplierAccountOptions.find(account => account.account === selectedSupplierAccount);
    return selected ? [selected, ...matchedOptions] : matchedOptions;
  }, [selectedSupplierAccount, supplierAccountOptions, supplierAccountSearch]);

  const handleSearch = useCallback(async (q, supplierAccount = selectedSupplierAccount) => {
    if (!q.trim() && !supplierAccount) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      if (supplierAccount) params.set('supplier_account', supplierAccount);
      const res = await fetch(`${API_BASE_URL}/dispatcher/schedules/search?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${getDispatcherToken()}` }
      });
      if (res.ok) setSearchResults(await res.json());
    } catch (err) {
      console.error('Search failed:', err);
    } finally { setSearching(false); }
  }, [selectedSupplierAccount]);

  useEffect(() => {
    if (activeSection !== 'search') return;
    const timeout = setTimeout(() => handleSearch(searchQuery, selectedSupplierAccount), 300);
    return () => clearTimeout(timeout);
  }, [searchQuery, selectedSupplierAccount, activeSection, handleSearch]);

  useEffect(() => {
    if (activeSection !== 'search') return;
    let cancelled = false;
    fetch(`${API_BASE_URL}/dispatcher/supplier-accounts`, {
      headers: { 'Authorization': `Bearer ${getDispatcherToken()}` }
    })
      .then(res => (res.ok ? res.json() : []))
      .then(data => {
        if (!cancelled) setSupplierAccounts(Array.isArray(data) ? data : []);
      })
      .catch(err => console.error('Failed to fetch supplier accounts:', err));
    return () => { cancelled = true; };
  }, [activeSection]);

  const handleSupplierAccountSearchChange = (value) => {
    setSupplierAccountSearch(value);
    const matched = supplierAccounts.find(account => buildSupplierAccountLabel(account) === value);
    setSelectedSupplierAccount(matched?.account || '');
  };

  const handleSupplierAccountSelectChange = (accountValue) => {
    setSelectedSupplierAccount(accountValue);
    const matched = supplierAccounts.find(account => account.account === accountValue);
    setSupplierAccountSearch(matched ? buildSupplierAccountLabel(matched) : '');
  };

  const clearSupplierAccountFilter = () => {
    setSupplierAccountSearch('');
    setSelectedSupplierAccount('');
  };

  // Stats
  const fetchStats = async (from, to) => {
    try {
      const res = await fetch(`${API_BASE_URL}/dispatcher/stats?date_from=${from}&date_to=${to}`, {
        headers: { 'Authorization': `Bearer ${getDispatcherToken()}` }
      });
      if (res.ok) setStatsData(await res.json());
    } catch (err) {
      console.error('Stats failed:', err);
    }
  };

  const fetchDriverMonthlyStats = useCallback(async (
    month = driverMonthlyStatsMonth,
    dispatcherId = driverMonthlyStatsDispatcherId,
    driverId = driverMonthlyStatsDriverId,
  ) => {
    setDriverMonthlyStatsLoading(true);
    try {
      const params = new URLSearchParams({ month });
      if (dispatcherId) params.set('dispatcher_id', dispatcherId);
      if (driverId) params.set('driver_id', driverId);
      const res = await fetch(`${API_BASE_URL}/dispatcher/drivers/monthly-stats?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${getDispatcherToken()}` }
      });
      if (res.ok) {
        setDriverMonthlyStats(await res.json());
      } else {
        setDriverMonthlyStats(null);
      }
    } catch (err) {
      console.error('Driver monthly stats failed:', err);
      setDriverMonthlyStats(null);
    } finally {
      setDriverMonthlyStatsLoading(false);
    }
  }, [driverMonthlyStatsDispatcherId, driverMonthlyStatsDriverId, driverMonthlyStatsMonth]);

  const handleDriverMonthlyStatsMonthChange = (month) => {
    setDriverMonthlyStatsMonth(month);
  };

  const handleDriverMonthlyStatsDispatcherChange = (dispatcherId) => {
    setDriverMonthlyStatsDispatcherId(dispatcherId);
    setDriverMonthlyStatsDriverId('');
  };

  const handleDriverMonthlyStatsDriverChange = (driverId) => {
    setDriverMonthlyStatsDriverId(driverId);
  };

  useEffect(() => {
    if (activeSection !== 'stats') return;
    const today = getTokyoDateKey();
    const tomorrow = addDaysToDateKey(today, 1);
    const week = addDaysToDateKey(today, 7);
    const { start: monthStart, end: monthEnd } = getTokyoMonthRange();
    
    let from = today, to = today;
    if (statsRange === 'month') { from = monthStart; to = monthEnd; }
    else if (statsRange === 'tomorrow') { from = tomorrow; to = tomorrow; }
    else if (statsRange === '7days') { to = week; }
    else if (statsRange === 'custom') { from = statsDateFrom || today; to = statsDateTo || today; }
    fetchStats(from, to);
  }, [activeSection, statsRange, statsDateFrom, statsDateTo]);

  useEffect(() => {
    if (activeSection !== 'stats') return;
    fetchDriverMonthlyStats(driverMonthlyStatsMonth, driverMonthlyStatsDispatcherId, driverMonthlyStatsDriverId);
  }, [activeSection, driverMonthlyStatsDispatcherId, driverMonthlyStatsDriverId, driverMonthlyStatsMonth, fetchDriverMonthlyStats]);

  return (
    <div className="dispatcher-luxury-portal dispatcher-modern min-h-screen">
      {/* Header */}
      <header className="dispatcher-luxury-header border-b border-[#d8b76a]/22">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="shonan-compact-brand">
              <div className="shonan-compact-brand-mark">
                <img
                  src={shonanPalmLogo}
                  alt="Shonan International"
                  className="h-full w-full object-contain"
                />
              </div>
              <div className="shonan-compact-brand-copy">
                <p className="shonan-compact-brand-title">SHONAN DISPATCHER</p>
                <p className="shonan-compact-brand-subtitle">Private Transfer & Charter</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <span className="rounded-full border border-[#d8b76a]/22 bg-black/35 px-3 py-1.5 text-sm text-[#f3d98b]">{dispatcher?.name}</span>
              <a
                href="/internal"
                className="dispatcher-luxury-subtle-button px-3 py-1.5 text-xs"
                data-testid="dispatcher-internal-entry-link"
                title="车辆司机管理后台"
                aria-label="车辆司机管理后台"
              >
                <Car size={18} aria-hidden="true" />
              </a>
              <button
                onClick={onLogout}
                className="dispatcher-luxury-subtle-button px-3 py-1.5 text-xs text-white/68"
                data-testid="dispatcher-logout-btn"
              >
                退出
              </button>
            </div>
          </div>
        </div>
        
        {/* Navigation */}
        <div className="max-w-7xl mx-auto px-4 pb-3">
          <nav className="dispatcher-luxury-toolbar flex gap-2 flex-wrap">
            <button
              onClick={() => setActiveSection('overview')}
              className={`px-3 py-1.5 text-xs dispatcher-luxury-tab ${
                activeSection === 'overview'
                  ? 'dispatcher-luxury-tab-active'
                  : ''
              }`}
            >
              <LayoutGrid size={18} aria-hidden="true" />调度总览
            </button>
            <button
              onClick={() => setActiveSection('search')}
              className={`px-3 py-1.5 text-xs dispatcher-luxury-tab ${
                activeSection === 'search'
                  ? 'dispatcher-luxury-tab-active'
                  : ''
              }`}
              data-testid="tab-search"
            >
              <Search size={18} aria-hidden="true" />搜索
            </button>
            <button
              onClick={() => setActiveSection('stats')}
              className={`px-3 py-1.5 text-xs dispatcher-luxury-tab ${
                activeSection === 'stats'
                  ? 'dispatcher-luxury-tab-active'
                  : ''
              }`}
              data-testid="tab-stats"
            >
              <BarChart3 size={18} aria-hidden="true" />统计
            </button>
            <Link 
              to="/dispatcher/schedules" 
              className="px-3 py-1.5 text-xs dispatcher-luxury-tab"
            >
              <ClipboardList size={18} aria-hidden="true" />我的追加
            </Link>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-5">

        {/* ========== SEARCH SECTION ========== */}
        {activeSection === 'search' && (
          <div className="space-y-4">
            <div className="premium-panel p-4 premium-toolbar">
              <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(260px,0.9fr)_auto]">
                <label className="grid gap-1.5" data-testid="dispatcher-customer-name-filter">
                  <span className="text-xs font-medium text-muted-foreground">客户名字</span>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="客户名字: 按客人姓名筛选"
                    className="premium-input"
                    aria-label="客户名字筛选"
                    data-testid="global-search"
                    autoFocus
                  />
                </label>
                <div className="grid gap-1.5">
                  <span className="text-xs font-medium text-muted-foreground">旅行社</span>
                  <input
                    type="text"
                    value={supplierAccountSearch}
                    onChange={(e) => handleSupplierAccountSearchChange(e.target.value)}
                    placeholder="旅行社账号: 按会社名/氏名筛选"
                    className="premium-input"
                    data-testid="dispatcher-supplier-account-name-search"
                  />
                  <select
                    value={selectedSupplierAccount}
                    onChange={(e) => handleSupplierAccountSelectChange(e.target.value)}
                    className="premium-input"
                    aria-label="按旅行社账号筛选"
                    data-testid="dispatcher-supplier-account-filter"
                  >
                    <option value="">全部旅行社账号</option>
                    {filteredSupplierAccountOptions.map(account => (
                      <option key={account.account} value={account.account}>
                        {buildSupplierAccountLabel(account)}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={clearSupplierAccountFilter}
                  className="premium-button bg-surface px-3 py-2 text-xs text-muted-foreground hover:bg-muted disabled:opacity-50 md:self-end"
                  disabled={!supplierAccountSearch && !selectedSupplierAccount}
                  data-testid="dispatcher-supplier-account-filter-clear"
                >
                  清除旅行社
                </button>
              </div>
              {selectedSupplierAccount && (
                <p className="mt-2 text-xs text-muted-foreground" data-testid="dispatcher-supplier-account-filter-selected">
                  当前旅行社：{selectedSupplierLabel || supplierAccountSearch}
                </p>
              )}
            </div>
            {searching && <p className="text-center text-muted-foreground text-sm py-4">搜索中...</p>}
            {!searching && searchResults.length === 0 && (searchQuery.trim() || selectedSupplierAccount) && (
              <p className="text-center text-muted-foreground text-sm py-8">无搜索结果</p>
            )}
            <div className="space-y-2">
              {searchResults.map((item, i) => (
                <div
                  key={item.id || i}
                  className="premium-card dispatch-owner-accent hover:bg-muted cursor-pointer transition"
                  style={{ '--dispatch-owner-color': scheduleOwner(item).color }}
                  onClick={() => setEditingItem(item)}
                >
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-medium text-sm text-foreground">{item.time || '--:--'}</span>
                    <span className="text-xs text-muted-foreground">{item.date}</span>
                    <span className={`px-1.5 py-0.5 rounded-full text-xs ${item._type === 'official' ? 'premium-chip' : 'premium-chip-success'}`}>{item._type === 'official' ? '正式' : '调度追加'}</span>
                    <span className={`px-1.5 py-0.5 rounded text-xs ${
                      item.service_type === 'charter' ? 'premium-chip-success' :
                      item.service_type === 'other' ? 'premium-chip' : 'premium-chip'
                    }`}>{item.service_type === 'charter' ? '包车' : item.service_type === 'other' ? '其他' : '接送'}</span>
                    {item.driver_name && <span className="text-xs text-muted-foreground">🚗 {item.driver_name}</span>}
                    {item.price_amount != null && (
                      <span className="text-xs text-muted-foreground">¥{item.price_amount.toLocaleString()} {item.price_currency}</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground" style={{overflowWrap:'anywhere'}}>
                    {item.pickup_location || '-'} → {item.dropoff_location || '-'}
                  </p>
                  <div className="flex gap-3 text-xs text-muted-foreground mt-1 flex-wrap">
                    <span style={{ color: scheduleOwner(item).color }}>{scheduleOwner(item).label}</span>
                    {item.order_no && <span>订单号 {item.order_no}</span>}
                    {item.customer_name && <span>👤 {item.customer_name}</span>}
                    {item.customer_code && <span>#{item.customer_code}</span>}
                    {item.customer_company && <span>🏢 {item.customer_company}</span>}
                    {item.flight_number && <span>✈ {item.flight_number}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ========== STATS SECTION ========== */}
        {activeSection === 'stats' && (
          <div className="space-y-4">
            {/* Stats time filter */}
            <div className="premium-card p-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-muted-foreground">时间范围:</span>
                {[
                  { key: 'month', label: '当月' },
                  { key: 'today', label: '今天' },
                  { key: 'tomorrow', label: '明天' },
                  { key: '7days', label: '7天' },
                  { key: 'custom', label: '自定义' },
                ].map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setStatsRange(opt.key)}
                    className={`px-3 py-1.5 text-xs premium-button ${
                      statsRange === opt.key ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-surface text-muted-foreground hover:bg-muted'
                    }`}
                  >{opt.label}</button>
                ))}
                {statsRange === 'custom' && (
                  <div className="flex gap-2 items-center">
                    <input type="date" value={statsDateFrom} onChange={e => setStatsDateFrom(e.target.value)}
                      className="w-32 premium-input text-xs" />
                    <span className="text-muted-foreground">-</span>
                    <input type="date" value={statsDateTo} onChange={e => setStatsDateTo(e.target.value)}
                      className="w-32 premium-input text-xs" />
                  </div>
                )}
              </div>
            </div>

            {statsData && (
              <>
                {/* Totals */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="premium-card p-4 text-center">
                    <p className="text-2xl font-bold text-foreground">{statsData.totals.order_count}</p>
                    <p className="text-xs text-muted-foreground mt-1">订单总数</p>
                  </div>
                  <div className="premium-card p-4 text-center">
                    <p className="text-2xl font-bold text-foreground">¥{statsData.totals.total_jpy?.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground mt-1">总金额 (JPY)</p>
                  </div>
                  {statsData.totals.total_cny > 0 && (
                    <div className="premium-card p-4 text-center">
                      <p className="text-2xl font-bold text-foreground">¥{statsData.totals.total_cny?.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground mt-1">总金额 (CNY)</p>
                    </div>
                  )}
                  {(statsData.totals.collection_jpy > 0 || statsData.totals.collection_cny > 0) && (
                    <div className="premium-card p-4 text-center">
                    <p className="text-2xl font-bold text-foreground">
                        ¥{(statsData.totals.collection_jpy || 0).toLocaleString()}
                        {statsData.totals.collection_cny > 0 && ` / ¥${statsData.totals.collection_cny.toLocaleString()}`}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">司机代收 (JPY{statsData.totals.collection_cny > 0 ? '/CNY' : ''})</p>
                    </div>
                  )}
                </div>

                {/* Per-driver stats */}
                <div className="premium-card">
                  <div className="p-3 border-b border-border-subtle">
                    <h3 className="text-sm font-semibold text-foreground">司机订单统计</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-surface text-xs text-muted-foreground">
                          <th className="text-left px-4 py-2 font-medium">司机</th>
                          <th className="text-right px-4 py-2 font-medium">订单数</th>
                          <th className="text-right px-4 py-2 font-medium">金额(JPY)</th>
                          <th className="text-right px-4 py-2 font-medium">金额(CNY)</th>
                          <th className="text-right px-4 py-2 font-medium">代收(JPY)</th>
                          <th className="text-right px-4 py-2 font-medium">代收(CNY)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {statsData.driver_stats.map(d => (
                          <tr key={d.driver_id} className="hover:bg-surface">
                            <td className="px-4 py-2 font-medium text-foreground">{d.driver_name}</td>
                            <td className="px-4 py-2 text-right text-muted-foreground">{d.order_count}</td>
                            <td className="px-4 py-2 text-right text-foreground font-medium">¥{d.total_jpy.toLocaleString()}</td>
                            <td className="px-4 py-2 text-right text-muted-foreground">{d.total_cny > 0 ? `¥${d.total_cny.toLocaleString()}` : '-'}</td>
                          <td className="px-4 py-2 text-right text-muted-foreground">{d.collection_jpy > 0 ? `¥${d.collection_jpy.toLocaleString()}` : '-'}</td>
                            <td className="px-4 py-2 text-right text-muted-foreground">{d.collection_cny > 0 ? `¥${d.collection_cny.toLocaleString()}` : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Per-dispatcher stats */}
                {statsData.dispatcher_stats.length > 0 && (
                  <div className="premium-card">
                    <div className="p-3 border-b border-border-subtle">
                      <h3 className="text-sm font-semibold text-foreground">调度员订单统计</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-surface text-xs text-muted-foreground">
                            <th className="text-left px-4 py-2 font-medium">调度员</th>
                            <th className="text-right px-4 py-2 font-medium">订单数</th>
                            <th className="text-right px-4 py-2 font-medium">金额(JPY)</th>
                            <th className="text-right px-4 py-2 font-medium">金额(CNY)</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {statsData.dispatcher_stats.map(d => (
                            <tr key={d.dispatcher_id} className="hover:bg-surface">
                              <td className="px-4 py-2 font-medium text-foreground">{d.dispatcher_name}</td>
                              <td className="px-4 py-2 text-right text-muted-foreground">{d.order_count}</td>
                              <td className="px-4 py-2 text-right text-foreground font-medium">¥{d.total_jpy.toLocaleString()}</td>
                              <td className="px-4 py-2 text-right text-muted-foreground">{d.total_cny > 0 ? `¥${d.total_cny.toLocaleString()}` : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}

            <DriverMonthlyHistoryPanel
              data={driverMonthlyStats}
              dispatcherId={driverMonthlyStatsDispatcherId}
              driverId={driverMonthlyStatsDriverId}
              loading={driverMonthlyStatsLoading}
              month={driverMonthlyStatsMonth}
              onDispatcherChange={handleDriverMonthlyStatsDispatcherChange}
              onDriverChange={handleDriverMonthlyStatsDriverChange}
              onMonthChange={handleDriverMonthlyStatsMonthChange}
              onRefresh={() => fetchDriverMonthlyStats(
                driverMonthlyStatsMonth,
                driverMonthlyStatsDispatcherId,
                driverMonthlyStatsDriverId,
              )}
            />
          </div>
        )}

        {/* ========== OVERVIEW SECTION ========== */}
        {activeSection === 'overview' && (<>
        {/* Controls */}
        <div className="dispatcher-overview-controls">
          <div>
            <div className="overview-actions">
              <button title="前一天" aria-label="前一天" onClick={() => setOverviewStartDate(addDaysToDateKey(overviewStartDate, -1))}><ChevronLeft size={18} /></button>
              <label><span className="sr-only">排班开始日期</span><input aria-label="排班开始日期" type="date" value={overviewStartDate} onChange={e => { if (e.target.value) setOverviewStartDate(e.target.value); }} /></label>
              <button title="后一天" aria-label="后一天" onClick={() => setOverviewStartDate(addDaysToDateKey(overviewStartDate, 1))}><ChevronRight size={18} /></button>
              <button onClick={() => setOverviewStartDate(getTokyoDateKey())}>今天</button>
              <button
                type="button"
                onClick={() => setShowDriverViewSettings(true)}
                title={`司机显示设置 · 显示${visibleOverviewDrivers.length} / 隐藏${hiddenOverviewDriverCount}`}
                aria-label="选择司机"
                data-testid="dispatcher-driver-view-settings-btn"
              >
                <SlidersHorizontal size={18} /><span>选择司机 ({visibleOverviewDrivers.length})</span>
              </button>
              <button
                type="button"
                onClick={fetchAllSchedules}
                title="刷新运行状况"
                aria-label="刷新运行状况"
              >
                <RefreshCw size={18} />
              </button>
            </div>
            
            {/* 仅在有创建权限时显示添加按钮 */}
            {dispatcher?.permissions?.can_create && (
              <button
                onClick={() => openAddModal()}
                className="px-3 py-1.5 bg-primary text-primary-foreground premium-button hover:brightness-105 flex items-center gap-1.5"
                data-testid="add-schedule-btn"
                title="添加调度排班"
                aria-label="添加调度排班"
              >
                <Plus size={18} />
              </button>
            )}
          </div>
        </div>

        {/* Schedule Overview Table */}
        {loading ? (
          <div className="premium-card p-8 text-center">
            <div className="animate-spin w-6 h-6 border-2 border-input border-t-gray-600 rounded-full mx-auto"></div>
            <p className="text-muted-foreground mt-3 text-sm">加载中...</p>
          </div>
        ) : drivers.length === 0 ? (
          <div className="premium-card p-8 text-center">
            <div className="w-12 h-12 bg-surface rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <h3 className="text-sm font-medium text-foreground">暂无可管理的司机</h3>
            <p className="text-muted-foreground mt-1 text-xs">请联系管理员分配司机</p>
          </div>
        ) : visibleOverviewDrivers.length === 0 ? (
          <div className="premium-card p-8 text-center">
            <h3 className="text-sm font-medium text-foreground">当前已隐藏全部司机</h3>
            <p className="text-muted-foreground mt-1 text-xs">打开司机显示设置，重新显示需要查看的司机。</p>
            <button
              type="button"
              onClick={() => setShowDriverViewSettings(true)}
              className="premium-button mt-4 bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground"
            >
              司机显示设置
            </button>
          </div>
        ) : (
          <DispatcherTimelineBoard
            drivers={visibleOverviewDrivers}
            days={dateColumns.slice(0, 3)}
            schedules={scheduleData}
            getItems={getDriverTimelineItems}
            dispatcher={dispatcher}
            onViewAll={openScheduleModal}
            onAssign={openAddModal}
            onOpen={setEditingItem}
          />
        )}
        </>)}
      </main>

      {/* Modals */}
      {showDriverViewSettings && (
        <DriverOverviewSettingsPanel
          drivers={orderedOverviewDrivers}
          hiddenMap={driverViewPreferences.hidden || {}}
          onClose={() => setShowDriverViewSettings(false)}
          onToggleHidden={toggleDriverHidden}
          onMove={moveDriverInPreference}
          onReset={resetDriverViewPreferences}
          onSetAll={(visible) => setDriverViewPreferences(current => ({ ...current, hidden: Object.fromEntries(drivers.map(driver => [driver.id, !visible])) }))}
        />
      )}

      {showScheduleModal && selectedDriver && (
        <DriverScheduleModal
          driver={selectedDriver}
          drivers={drivers}
          dispatcher={dispatcher}
          onClose={() => {
            setShowScheduleModal(false);
            setSelectedDriver(null);
          }}
          onAddSchedule={() => {
            setShowScheduleModal(false);
            openAddModal(selectedDriver);
          }}
          onRefresh={fetchAllSchedules}
        />
      )}

      {showAddModal && (
        <AddScheduleModal
          driver={selectedDriver}
          drivers={drivers}
          dispatcher={dispatcher}
          onClose={() => {
            setShowAddModal(false);
            setSelectedDriver(null);
          }}
          onSuccess={() => {
            setShowAddModal(false);
            setSelectedDriver(null);
            fetchAllSchedules();
          }}
        />
      )}

      {/* Edit modal from search */}
      {editingItem && (
        <EditScheduleModal
          schedule={editingItem}
          drivers={drivers}
          dispatcher={dispatcher}
          onClose={() => setEditingItem(null)}
          onSaved={() => {
            setEditingItem(null);
            handleSearch(searchQuery, selectedSupplierAccount);
            fetchAllSchedules();
          }}
        />
      )}
    </div>
  );
}

function DriverOverviewSettingsPanel({
  drivers,
  hiddenMap,
  onClose,
  onToggleHidden,
  onMove,
  onReset,
  onSetAll,
}) {
  const visibleCount = drivers.filter(driver => !hiddenMap[driver.id]).length;
  const hiddenCount = drivers.length - visibleCount;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-3">
      <div className="w-full max-w-2xl overflow-hidden rounded-xl border border-border-subtle bg-card text-card-foreground shadow-2xl">
        <div className="flex items-center justify-between border-b border-border-subtle bg-surface px-4 py-3">
          <div>
            <h3 className="text-base font-semibold text-foreground">司机显示设置</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              显示司机 {visibleCount} 位，隐藏司机 {hiddenCount} 位。这里的顺序只影响你的调度总览。
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-2 py-1 text-sm text-muted-foreground hover:bg-muted"
          >
            关闭
          </button>
        </div>
        <div className="max-h-[70dvh] overflow-y-auto p-4">
          <div className="mb-3 flex gap-3">
            <button type="button" className="premium-button px-3 py-2" onClick={() => onSetAll(true)}>全选司机</button>
            <button type="button" className="premium-button px-3 py-2" onClick={() => onSetAll(false)}>全部取消</button>
          </div>
          <div className="mb-3 flex items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">用上移/下移调整你喜欢的司机顺序；隐藏后不会删除司机。</p>
            <button
              type="button"
              onClick={onReset}
              className="premium-button bg-surface px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:bg-muted"
            >
              恢复默认排序
            </button>
          </div>
          <div className="space-y-2">
            {drivers.map((driver, index) => {
              const isHidden = Boolean(hiddenMap[driver.id]);
              return (
                <div
                  key={driver.id}
                  className={`flex flex-col gap-3 rounded-lg border px-3 py-3 sm:flex-row sm:items-center sm:justify-between ${
                    isHidden ? 'border-border-subtle bg-background/40 opacity-70' : 'border-border-subtle bg-background'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <input type="checkbox" checked={!isHidden} onChange={() => onToggleHidden(driver.id)} aria-label={`显示司机 ${driver.name || '未命名司机'}`} />
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-surface text-xs font-semibold text-foreground">
                      {index + 1}
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-foreground">{driver.name || '未命名司机'}</p>
                      <p className="text-xs text-muted-foreground">{driver.phone || '-'}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => onMove(driver.id, -1)}
                      disabled={index === 0}
                      className="premium-button bg-surface px-2.5 py-1 text-xs text-muted-foreground hover:bg-muted disabled:opacity-40"
                    >
                      上移
                    </button>
                    <button
                      type="button"
                      onClick={() => onMove(driver.id, 1)}
                      disabled={index === drivers.length - 1}
                      className="premium-button bg-surface px-2.5 py-1 text-xs text-muted-foreground hover:bg-muted disabled:opacity-40"
                    >
                      下移
                    </button>
                    <button
                      type="button"
                      onClick={() => onToggleHidden(driver.id)}
                      className={`premium-button px-3 py-1 text-xs font-semibold ${
                        isHidden
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-surface text-muted-foreground hover:bg-muted'
                      }`}
                    >
                      {isHidden ? '显示司机' : '隐藏司机'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export function DriverAvailabilityCard({ driver, days, schedule, canCreate, onViewFuture, onAssign, onSelectTimelineItem }) {
  const daySummaries = days.map(day => ({
    ...day,
    items: getDriverTimelineItems(schedule, day.date),
  }));
  const totalItems = daySummaries.reduce((sum, day) => sum + day.items.length, 0);
  const todayItems = daySummaries[0]?.items || [];
  const hasRest = todayItems.some(item => item.timeline_type === 'rest');
  const recommendation = hasRest
    ? { text: '休息，需核对时段', className: 'bg-amber-500/15 text-amber-200 border-amber-400/30' }
    : totalItems === 0
    ? { text: '推荐派单', className: 'bg-success/15 text-success border-success/30' }
    : todayItems.length <= 1
      ? { text: '可考虑', className: 'bg-primary/10 text-primary border-primary/30' }
      : { text: '谨慎', className: 'bg-amber-500/15 text-amber-200 border-amber-400/30' };
  const vehicleLabel = driver.vehicle_type || driver.vehicle || driver.car_type || '车辆未设置';

  return (
    <section className="premium-card overflow-hidden" data-testid={`driver-availability-${driver.id}`}>
      <div className="flex flex-col gap-3 border-b border-border-subtle p-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-background text-sm font-semibold text-foreground">
            {driver.name?.charAt(0) || '?'}
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-sm font-semibold text-foreground">{driver.name}</h3>
              <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${recommendation.className}`}>
                {recommendation.text}
              </span>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {driver.phone || '-'} · {vehicleLabel} · 三天排班 {totalItems} 件
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onViewFuture}
            className="premium-button bg-surface px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:bg-muted"
          >
            查看全部排班
          </button>
          {canCreate && (
            <button
              type="button"
              onClick={onAssign}
              className="premium-button bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:brightness-105"
            >
              立即派单
            </button>
          )}
        </div>
      </div>
      <div className="space-y-3 p-4">
        {daySummaries.map(day => (
          <DriverDayTimeline
            key={day.date}
            day={day}
            onSelectTimelineItem={onSelectTimelineItem}
          />
        ))}
      </div>
    </section>
  );
}

function getDriverTimelineItems(schedule, date) {
  if (!schedule) return [];
  const officialOrders = (schedule.official_orders || [])
    .filter(item => item.date === date)
    .map(item => ({ ...item, timeline_type: 'official' }));
  const extraSchedules = (schedule.extra_schedules || [])
    .filter(item => item.date === date && item.status !== 'cancelled' && item.status !== '已取消')
    .map(item => ({ ...item, timeline_type: item.source_type || 'extra' }));

  const rest = (schedule.time_off || []).filter(item => !['cancelled', 'canceled', '已取消', 'deleted'].includes(item.status) && item.start_date <= date && (item.end_date || item.start_date) >= date)
    .map(item => ({ ...item, date, timeline_type: 'rest', time: item.all_day !== false ? '06:00' : item.start_time }));
  return [...officialOrders, ...extraSchedules, ...rest].sort((a, b) => (a.time || '99:99').localeCompare(b.time || '99:99'));
}

function DriverMonthlyHistoryPanel({
  data,
  dispatcherId,
  driverId,
  loading,
  month,
  onDispatcherChange,
  onDriverChange,
  onMonthChange,
  onRefresh,
}) {
  const [expandedDrivers, setExpandedDrivers] = useState({});
  const [selectedSourceBucket, setSelectedSourceBucket] = useState('');
  const totals = useMemo(() => data?.totals || {}, [data]);
  const drivers = useMemo(() => data?.drivers || [], [data]);
  const filterOptions = data?.filter_options || {};
  const dispatchers = filterOptions.dispatchers || [];
  const allDrivers = filterOptions.drivers || [];
  const availableDrivers = allDrivers;
  const selectedSourceLabel = getSourceLabel(selectedSourceBucket) === '来源' ? '全部来源' : getSourceLabel(selectedSourceBucket);
  const sourceRows = useMemo(() => (
    [
      { key: 'dispatcher_added', label: '调度加入', tone: 'blue' },
      { key: 'driver_added', label: '司机加入', tone: 'emerald' },
      { key: 'admin_added', label: '管理员加入', tone: 'amber' },
    ].map(row => ({
      ...row,
      ...(totals.source_breakdown?.[row.key] || {}),
    }))
  ), [totals]);
  const totalSourceOrderCount = sourceRows.reduce((sum, row) => sum + (row.order_count || 0), 0);
  const visibleDrivers = useMemo(() => {
    if (!selectedSourceBucket) return drivers;

    return drivers
      .map(driver => {
        const sourceStat = driver.source_breakdown?.[selectedSourceBucket] || {};
        const filteredOrders = (driver.orders || []).filter(order => order.source_bucket === selectedSourceBucket);
        const orderCount = sourceStat.order_count || filteredOrders.length;
        if (orderCount === 0) return null;

        const transferCount = filteredOrders.filter(order => order.service_type !== 'charter').length;
        const charterCount = filteredOrders.filter(order => order.service_type === 'charter').length;

        return {
          ...driver,
          order_count: orderCount,
          total_amount: sourceStat.total_amount,
          total_cny: sourceStat.total_cny,
          collection_jpy: sourceStat.collection_jpy,
          collection_cny: sourceStat.collection_cny,
          transfer_count: transferCount,
          charter_count: charterCount,
          orders: filteredOrders,
        };
      })
      .filter(Boolean);
  }, [drivers, selectedSourceBucket]);
  const visibleTotals = useMemo(() => {
    if (!selectedSourceBucket) return totals;
    const sourceStat = totals.source_breakdown?.[selectedSourceBucket] || {};
    return {
      ...totals,
      order_count: sourceStat.order_count || 0,
      total_amount: sourceStat.total_amount,
      total_cny: sourceStat.total_cny,
      collection_jpy: sourceStat.collection_jpy,
      collection_cny: sourceStat.collection_cny,
      transfer_count: visibleDrivers.reduce((sum, driver) => sum + (driver.transfer_count || 0), 0),
      charter_count: visibleDrivers.reduce((sum, driver) => sum + (driver.charter_count || 0), 0),
    };
  }, [selectedSourceBucket, totals, visibleDrivers]);

  const toggleDriver = (driverId) => {
    setExpandedDrivers(current => ({
      ...current,
      [driverId]: !current[driverId],
    }));
  };

  return (
    <div className="premium-card overflow-hidden" data-testid="dispatcher-source-first-dashboard">
      <div className="border-b border-border-subtle p-4">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="max-w-xl">
            <h3 className="text-base font-semibold text-foreground">开放司机全来源月度统计</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              来源对比总览：先看调度加入、司机加入、管理员加入的当月金额和来源占比，再核对司机订单明细。
            </p>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5 xl:min-w-[820px]">
            <MonthSelect
              value={month}
              onChange={onMonthChange}
              className="premium-input h-11 text-xs"
              dataTestId="dispatcher-driver-monthly-stats-month-select"
            />
            <select
              value={dispatcherId}
              onChange={(event) => onDispatcherChange(event.target.value)}
              className="premium-input h-11 text-xs"
              data-testid="dispatcher-driver-monthly-stats-dispatcher-select"
            >
              <option value="">全部调度</option>
              {dispatchers.map(item => (
                <option key={item.id} value={item.id}>
                  {item.name || item.phone || '未命名调度'}
                </option>
              ))}
            </select>
            <select
              value={driverId}
              onChange={(event) => onDriverChange(event.target.value)}
              className="premium-input h-11 text-xs"
              data-testid="dispatcher-driver-monthly-stats-driver-select"
            >
              <option value="">全部司机</option>
              {availableDrivers.map(driver => (
                <option key={driver.id} value={driver.id}>
                  {driver.name || driver.phone || '未命名司机'}
                </option>
              ))}
            </select>
            <select
              value={selectedSourceBucket}
              onChange={(event) => setSelectedSourceBucket(event.target.value)}
              className="premium-input h-11 text-xs"
              data-testid="dispatcher-driver-monthly-stats-source-select"
            >
              {SOURCE_FILTER_OPTIONS.map(option => (
                <option key={option.key || 'all'} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={onRefresh}
              disabled={loading}
              className="premium-button h-11 bg-primary px-4 text-xs font-semibold text-primary-foreground hover:brightness-105 disabled:opacity-50"
            >
              {loading ? '刷新中...' : '刷新月度统计'}
            </button>
          </div>
        </div>
      </div>

      <div className="p-4">
        <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-primary">来源对比总览</p>
            <h4 className="text-lg font-semibold text-foreground">{month.replace('-', '年')}月</h4>
          </div>
          <p className="text-xs text-muted-foreground">
            来源筛选：{selectedSourceLabel}。点击来源卡或使用下拉框可切换统计口径。
          </p>
        </div>
        <div className="grid gap-3 lg:grid-cols-3">
          {sourceRows.map(row => (
            <SourceFirstSourceCard
              key={row.key}
              row={row}
              selected={selectedSourceBucket === row.key}
              totalSourceOrderCount={totalSourceOrderCount}
              onSelect={() => setSelectedSourceBucket(selectedSourceBucket === row.key ? '' : row.key)}
            />
          ))}
        </div>
      </div>

      <div className="mx-4 mb-4 rounded-xl border border-primary/25 bg-primary/10 p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs font-medium text-primary">当前筛选合计 / 当前来源合计</p>
            <h4 className="mt-1 text-xl font-semibold text-foreground">{selectedSourceLabel}</h4>
          </div>
          <div className="grid flex-1 grid-cols-2 gap-3 sm:grid-cols-3 lg:max-w-4xl lg:grid-cols-6">
            <MonthlyStatTile label="订单数" value={visibleTotals.order_count || 0} />
            <MonthlyStatTile label="金额(JPY)" value={visibleTotals.total_amount === null ? '无权限' : formatYen(visibleTotals.total_amount)} />
            <MonthlyStatTile label="金额(CNY)" value={visibleTotals.total_cny === null ? '无权限' : `¥${Math.round(visibleTotals.total_cny || 0).toLocaleString()}`} />
            <MonthlyStatTile label="司机代收(JPY)" value={visibleTotals.collection_jpy === null ? '无权限' : formatYen(visibleTotals.collection_jpy)} />
            <MonthlyStatTile label="接送订单" value={visibleTotals.transfer_count || 0} />
            <MonthlyStatTile label="包车订单" value={visibleTotals.charter_count || 0} />
          </div>
        </div>
      </div>

      {(totals.hidden_price_order_count || 0) > 0 && (
        <div className="mx-4 mb-4 rounded-lg border border-cyan-400/40 bg-cyan-500/10 px-3 py-2 text-xs text-cyan-200">
          有 {totals.hidden_price_order_count} 条订单金额因权限设置未显示，合计金额只计算当前调度可查看的部分。
        </div>
      )}

      <div className="px-4 pb-4">
        <div className="rounded-xl border border-border-subtle bg-background/40">
          <div className="flex items-center justify-between border-b border-border-subtle px-4 py-3">
            <div>
              <h4 className="text-sm font-semibold text-foreground">司机订单明细</h4>
              <p className="text-xs text-muted-foreground">当前列表共 {visibleDrivers.length} 位司机</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface text-xs text-muted-foreground">
                  <th className="px-4 py-3 text-left font-medium">司机</th>
                  <th className="px-4 py-3 text-right font-medium">订单数</th>
                  <th className="px-4 py-3 text-right font-medium">金额(JPY)</th>
                  <th className="px-4 py-3 text-right font-medium">金额(CNY)</th>
                  <th className="px-4 py-3 text-right font-medium">司机代收</th>
                  <th className="px-4 py-3 text-right font-medium">接送/包车</th>
                  <th className="px-4 py-3 text-right font-medium">订单明细</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-subtle">
                {loading && drivers.length === 0 && (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-muted-foreground">读取月度统计中...</td>
                  </tr>
                )}
                {!loading && visibleDrivers.length === 0 && (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-muted-foreground">这个月份暂无司机订单</td>
                  </tr>
                )}
                {visibleDrivers.map(driver => {
                  const isExpanded = Boolean(expandedDrivers[driver.driver_id]);
                  const canViewPrice = driver.can_view_price !== false;
                  return (
                    <tr key={driver.driver_id} className="align-top">
                      <td colSpan="7" className="p-0">
                        <div className="grid grid-cols-1 gap-2 px-4 py-3 hover:bg-surface md:grid-cols-[1.4fr_0.7fr_1fr_1fr_1fr_0.8fr_0.9fr] md:items-center">
                          <div>
                            <div className="font-medium text-foreground">{driver.driver_name}</div>
                            <div className="text-xs text-muted-foreground">{driver.driver_phone || '-'}</div>
                          </div>
                          <div className="text-left text-muted-foreground md:text-right">{driver.order_count || 0}</div>
                          <div className="text-left font-semibold text-foreground md:text-right">
                            {formatVisibleYen(driver.total_amount, canViewPrice)}
                          </div>
                          <div className="text-left font-semibold text-emerald-400 md:text-right">
                            {!canViewPrice ? '无权限' : `¥${Math.round(driver.total_cny || 0).toLocaleString()}`}
                          </div>
                          <div className="text-left text-muted-foreground md:text-right">
                            {!canViewPrice
                              ? '无权限'
                              : `${formatYen(driver.collection_jpy)} / ¥${Math.round(driver.collection_cny || 0).toLocaleString()} CNY`}
                          </div>
                          <div className="text-left text-muted-foreground md:text-right">
                            {driver.transfer_count || 0} / {driver.charter_count || 0}
                          </div>
                          <div className="text-left md:text-right">
                            <button
                              type="button"
                              onClick={() => toggleDriver(driver.driver_id)}
                              disabled={(driver.orders || []).length === 0}
                              className="premium-button bg-primary/15 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/20 disabled:opacity-50"
                            >
                              {isExpanded ? '收起订单' : `查看订单 (${(driver.orders || []).length})`}
                            </button>
                          </div>
                        </div>
                        {isExpanded && (
                          <DispatcherMonthlyOrderDetails
                            orders={driver.orders || []}
                            canViewPrice={canViewPrice}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function SourceFirstSourceCard({ row, selected, totalSourceOrderCount, onSelect }) {
  const sourceSharePercent = totalSourceOrderCount > 0
    ? Math.round(((row.order_count || 0) / totalSourceOrderCount) * 100)
    : 0;
  const toneClasses = {
    blue: {
      card: selected ? 'border-blue-400 bg-blue-500/15' : 'border-blue-400/30 bg-blue-500/10',
      badge: 'bg-blue-500/20 text-blue-200',
      bar: 'bg-blue-400',
      text: 'text-blue-200',
    },
    emerald: {
      card: selected ? 'border-emerald-400 bg-emerald-500/15' : 'border-emerald-400/30 bg-emerald-500/10',
      badge: 'bg-emerald-500/20 text-emerald-200',
      bar: 'bg-emerald-400',
      text: 'text-emerald-200',
    },
    amber: {
      card: selected ? 'border-amber-400 bg-amber-500/15' : 'border-amber-400/30 bg-amber-500/10',
      badge: 'bg-amber-500/20 text-amber-200',
      bar: 'bg-amber-400',
      text: 'text-amber-200',
    },
  }[row.tone] || {
    card: selected ? 'border-primary bg-primary/10' : 'border-border-subtle bg-surface',
    badge: 'bg-primary/10 text-primary',
    bar: 'bg-primary',
    text: 'text-primary',
  };

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`rounded-xl border p-4 text-left transition hover:-translate-y-0.5 hover:shadow-lg ${toneClasses.card}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className={`text-sm font-semibold ${toneClasses.text}`}>{row.label}</p>
          <p className="mt-3 text-2xl font-bold text-foreground">
            {row.total_amount === null || row.total_amount === undefined ? '无权限' : formatYen(row.total_amount)}
          </p>
          {(row.total_cny || 0) > 0 && (
            <p className="mt-1 text-xs text-muted-foreground">CNY ¥{Math.round(row.total_cny || 0).toLocaleString()}</p>
          )}
        </div>
        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${toneClasses.badge}`}>
          {row.order_count || 0} 单
        </span>
      </div>
      <div className="mt-4">
        <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
          <span>来源占比</span>
          <span>{sourceSharePercent}%</span>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-background">
          <div
            className={`h-full rounded-full ${toneClasses.bar}`}
            style={{ width: `${sourceSharePercent}%` }}
          />
        </div>
      </div>
    </button>
  );
}

function MonthlyStatTile({ label, value }) {
  return (
    <div className="rounded-lg border border-border-subtle bg-background/60 px-3 py-3 text-center">
      <p className="text-lg font-bold text-foreground">{value}</p>
      <p className="mt-1 text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

function MonthlySourceBreakdown({ sourceBreakdown, selectedSourceBucket }) {
  const rows = [
    { key: 'dispatcher_added', label: '调度加入' },
    { key: 'driver_added', label: '司机加入' },
    { key: 'admin_added', label: '管理员加入' },
  ];

  return (
    <div className="grid gap-3 px-4 pb-4 md:grid-cols-3" data-testid="dispatcher-source-breakdown">
      {rows.map(row => {
        const item = sourceBreakdown[row.key] || {};
        const amount = item.total_amount;
        const isSelected = selectedSourceBucket === row.key;
        return (
          <div
            key={row.key}
            className={`rounded-lg border px-4 py-3 ${
              isSelected
                ? 'border-primary bg-primary/10'
                : 'border-border-subtle bg-surface'
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold text-foreground">{row.label}</p>
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                {item.order_count || 0} 单
              </span>
            </div>
            <p className="mt-2 text-xl font-bold text-foreground">
              {amount === null || amount === undefined ? '无权限' : formatYen(amount)}
            </p>
            {(item.total_cny || 0) > 0 && (
              <p className="mt-1 text-xs text-muted-foreground">CNY ¥{Math.round(item.total_cny || 0).toLocaleString()}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

function DispatcherMonthlyOrderDetails({ orders, canViewPrice }) {
  if (orders.length === 0) {
    return (
      <div className="mx-4 mb-4 rounded-lg border border-border-subtle bg-background px-4 py-3 text-sm text-muted-foreground">
        该司机本月暂无订单明细
      </div>
    );
  }

  return (
    <div className="mx-4 mb-4 overflow-x-auto rounded-lg border border-border-subtle bg-background">
      <table className="w-full text-xs">
        <thead className="bg-muted/70 text-muted-foreground">
          <tr>
            <th className="px-3 py-2 text-left font-medium">订单号</th>
            <th className="px-3 py-2 text-left font-medium">来源</th>
            <th className="px-3 py-2 text-left font-medium">日期时间</th>
            <th className="px-3 py-2 text-left font-medium">客户名称</th>
            <th className="px-3 py-2 text-left font-medium">客户代码</th>
            <th className="px-3 py-2 text-left font-medium">路线概要</th>
            <th className="px-3 py-2 text-left font-medium">状态</th>
            <th className="px-3 py-2 text-right font-medium">订单金额</th>
            <th className="px-3 py-2 text-right font-medium">司机代收</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border-subtle">
          {orders.map(order => (
            <tr key={order.order_id || order.order_no} className="hover:bg-muted/50">
              <td className="px-3 py-2 font-medium text-foreground">{order.order_no || '-'}</td>
              <td className="px-3 py-2 text-muted-foreground">{getSourceLabel(order.source_bucket)}</td>
              <td className="px-3 py-2 text-muted-foreground">
                {[order.departure_date, order.pickup_time].filter(Boolean).join(' ') || '-'}
              </td>
              <td className="px-3 py-2 text-foreground">{order.customer_name || '-'}</td>
              <td className="px-3 py-2 text-foreground">{order.customer_code || '-'}</td>
              <td className="px-3 py-2 text-muted-foreground">{order.route_summary || '-'}</td>
              <td className="px-3 py-2 text-muted-foreground">{order.status || '-'}</td>
              <td className="px-3 py-2 text-right font-semibold text-foreground">
                {!canViewPrice ? '无权限' : `¥${Math.round(order.amount || 0).toLocaleString()} ${order.amount_currency || 'JPY'}`}
              </td>
              <td className="px-3 py-2 text-right font-semibold text-emerald-700">
                {!canViewPrice
                  ? '无权限'
                  : order.driver_collection_amount
                    ? `¥${Math.round(order.driver_collection_amount || 0).toLocaleString()} ${order.driver_collection_currency || 'JPY'}`
                    : '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
